<template>
  <div class="analyze-year-review">
    <div class="container">

      <!-- 年份選擇 -->
      <div class="year-tabs" v-if="availableYears.length">
        <button
          v-for="y in availableYears"
          :key="y"
          class="year-tab"
          :class="{ active: y === selectedYear }"
          @click="selectedYear = y"
        >{{ y }}</button>
      </div>

      <!-- Hero -->
      <section class="hero-section">
        <div class="hero-row">
          <div class="hero-stat">
            <div class="hero-num">{{ outfitsThisYear.length }}</div>
            <div class="hero-label fs-14px">套穿搭紀錄</div>
          </div>
          <div class="hero-divider"></div>
          <div class="hero-stat">
            <div class="hero-num">{{ clothsAddedThisYear.length }}</div>
            <div class="hero-label fs-14px">件新增單品</div>
          </div>
        </div>
        <p class="hero-msg fs-14px">{{ heroMsg }}</p>
      </section>

      <!-- Cards grid -->
      <div class="layout-grid" v-if="outfitsThisYear.length > 0 || clothsAddedThisYear.length > 0">

        <!-- 最常穿的單品 top 3 -->
        <section class="review-card span-2" v-if="topCloths.length > 0">
          <h3 class="section-title fs-16px fw-bold">穿最多次的單品</h3>
          <div class="top-cloths">
            <div v-for="(c, i) in topCloths" :key="c.serial" class="top-cloth-row">
              <div class="rank fs-h2 fw-bold">{{ i + 1 }}</div>
              <div class="cloth-thumb" :style="thumbStyle(c)"></div>
              <div class="cloth-info">
                <div class="cloth-name fs-14px fw-bold">{{ c.name || '未命名單品' }}</div>
                <div class="cloth-meta fs-12px">{{ c.wearCountThisYear }} 次</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 月份分布 -->
        <section class="review-card span-2" v-if="totalMonthlyCount > 0">
          <h3 class="section-title fs-16px fw-bold">每月穿搭次數</h3>
          <div class="monthly-bars">
            <div v-for="(count, idx) in monthlyOutfitCounts" :key="idx" class="month-col">
              <div class="month-bar-wrap">
                <div
                  class="month-bar"
                  :style="{ height: barHeight(count) }"
                  :title="`${idx + 1} 月：${count} 套`"
                ></div>
              </div>
              <div class="month-label fs-12px">{{ idx + 1 }}</div>
            </div>
          </div>
          <div class="monthly-summary fs-12px">
            最高峰：{{ peakMonth.label }}（{{ peakMonth.count }} 套）
          </div>
        </section>

        <!-- 最常的場景 -->
        <section class="review-card" v-if="topSituations.length > 0">
          <h3 class="section-title fs-16px fw-bold">最常的場景</h3>
          <div class="rank-bar-list">
            <div v-for="(s, i) in topSituationsWithPct" :key="s.name" class="rank-bar-row">
              <div class="rank-bar-name fs-14px">{{ s.name }}</div>
              <CustomHorizontalProgressBar
                :percentage="s.pct"
                :count="s.count"
                unit=" 套"
                :color="i === 0 ? '#EF7B1B' : '#F8B25C'"
              />
            </div>
          </div>
        </section>

        <!-- 最常穿的色系 -->
        <section class="review-card" v-if="topColors.length > 0">
          <h3 class="section-title fs-16px fw-bold">最常穿的色系</h3>
          <div class="rank-bar-list">
            <div v-for="c in topColorsWithPct" :key="c.id" class="rank-bar-row">
              <div class="rank-bar-name fs-14px d-flex align-items-center">
                <span
                  class="color-dot"
                  :style="{ backgroundColor: c.hex, borderColor: c.id === 0 ? '#E9E9E9' : 'transparent' }"
                ></span>
                <span>{{ c.name }}</span>
              </div>
              <CustomHorizontalProgressBar
                :percentage="c.pct"
                :count="c.count"
                unit=" 次"
                :color="c.hex === '#FFFFFF' ? '#E9E9E9' : c.hex"
              />
            </div>
          </div>
        </section>

        <!-- 最常的溫度區間 -->
        <section class="review-card" v-if="topTemperature">
          <h3 class="section-title fs-16px fw-bold">最常的溫度區間</h3>
          <div class="temp-display">
            <div class="temp-label fs-h2 fw-bold">{{ topTemperature.label }}</div>
            <div class="temp-count fs-12px">{{ topTemperature.count }} 套</div>
          </div>
        </section>

        <!-- 喜歡的穿搭比例 -->
        <section class="review-card" v-if="totalRatedOutfits > 0">
          <h3 class="section-title fs-16px fw-bold">穿搭好感度</h3>
          <div class="feeling-bar">
            <div class="feeling-segment liked" :style="{ width: feelingPct.liked + '%' }"></div>
            <div class="feeling-segment neutral" :style="{ width: feelingPct.neutral + '%' }"></div>
            <div class="feeling-segment disliked" :style="{ width: feelingPct.disliked + '%' }"></div>
          </div>
          <div class="feeling-legend fs-12px">
            <span><span class="dot liked"></span>喜歡 {{ feelingDist.liked }}</span>
            <span><span class="dot neutral"></span>普通 {{ feelingDist.neutral }}</span>
            <span><span class="dot disliked"></span>不喜歡 {{ feelingDist.disliked }}</span>
          </div>
        </section>

      </div>

      <!-- 空狀態 -->
      <section class="empty-state" v-else>
        <p class="fs-14px">{{ selectedYear }} 年還沒有穿搭紀錄，先記錄一些穿搭再回來看看吧！</p>
      </section>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CustomHorizontalProgressBar from '@/components/CustomHorizontalProgressBar.vue';

const COLOR_MAP = {
  0: { name: '白色', hex: '#FFFFFF' },
  1: { name: '黑色', hex: '#000000' },
  2: { name: '灰色', hex: '#6F6C6C' },
  3: { name: '茶色', hex: '#7C614F' },
  4: { name: '米色', hex: '#DDD1B1' },
  5: { name: '綠色', hex: '#AAD272' },
  6: { name: '藍色', hex: '#6CBDE5' },
  7: { name: '紫色', hex: '#A791E9' },
  8: { name: '黃色', hex: '#F8D265' },
  9: { name: '粉紅色', hex: '#EDB3D5' },
  10: { name: '紅色', hex: '#DE6167' },
  11: { name: '橘色', hex: '#EA864F' },
  12: { name: '銀色', hex: '#D6D6D6' },
  13: { name: '金色', hex: '#F5CB6B' },
  14: { name: '其他', hex: '#444444' },
};

const TEMP_LABELS = {
  0: '-10 ~ 0 ℃', 1: '1 ~ 10 ℃', 2: '11 ~ 15 ℃', 3: '16 ~ 20 ℃',
  4: '21 ~ 25 ℃', 5: '26 ~ 30 ℃', 6: '31 ~ 35 ℃', 7: '36 ~ 40 ℃',
};

export default {
  name: 'AnalyzeYearReview',
  components: {
    CustomHorizontalProgressBar,
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
    };
  },
  computed: {
    ...mapState(['outfits', 'cloths']),

    availableYears() {
      const years = new Set();
      for (const o of (this.outfits || [])) {
        if (o.date && o.available) {
          const y = parseInt(o.date.slice(0, 4), 10);
          if (!isNaN(y)) years.add(y);
        }
      }
      for (const c of (this.cloths || [])) {
        if (c.create_at) {
          const y = new Date(c.create_at).getFullYear();
          if (!isNaN(y)) years.add(y);
        }
      }
      return [...years].sort((a, b) => b - a);
    },

    outfitsThisYear() {
      return (this.outfits || []).filter(o =>
        o.available && o.date && o.date.startsWith(`${this.selectedYear}-`)
      );
    },

    clothsAddedThisYear() {
      return (this.cloths || []).filter(c => {
        if (!c.create_at) return false;
        return new Date(c.create_at).getFullYear() === this.selectedYear;
      });
    },

    clothWearCountMap() {
      const map = new Map();
      for (const o of this.outfitsThisYear) {
        for (const p of (o.outfitPhotos || [])) {
          if (p.type === 'cloth') {
            map.set(p.clothSerial, (map.get(p.clothSerial) || 0) + 1);
          }
        }
      }
      return map;
    },

    topCloths() {
      const sorted = [...this.clothWearCountMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
      return sorted.map(([serial, count]) => {
        const cloth = (this.cloths || []).find(c => c.serial === serial);
        if (!cloth) return null;
        return { ...cloth, wearCountThisYear: count };
      }).filter(Boolean);
    },

    topColors() {
      const counts = new Map();
      for (const [serial, wearCount] of this.clothWearCountMap.entries()) {
        const cloth = (this.cloths || []).find(c => c.serial === serial);
        if (cloth && cloth.color !== undefined && cloth.color !== -1) {
          counts.set(cloth.color, (counts.get(cloth.color) || 0) + wearCount);
        }
      }
      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id, count]) => ({ id, count, ...(COLOR_MAP[id] || { name: '未知', hex: '#CCC' }) }));
    },
    topColorsWithPct() {
      if (this.topColors.length === 0) return [];
      const max = this.topColors[0].count;
      return this.topColors.map(c => ({
        ...c,
        pct: max > 0 ? Math.round(c.count / max * 100) : 0,
      }));
    },

    topSituations() {
      const counts = new Map();
      for (const o of this.outfitsThisYear) {
        if (o.situation && o.situation.trim()) {
          counts.set(o.situation, (counts.get(o.situation) || 0) + 1);
        }
      }
      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
    },
    topSituationsWithPct() {
      if (this.topSituations.length === 0) return [];
      const max = this.topSituations[0].count;
      return this.topSituations.map(s => ({
        ...s,
        pct: max > 0 ? Math.round(s.count / max * 100) : 0,
      }));
    },

    topTemperature() {
      const counts = new Map();
      for (const o of this.outfitsThisYear) {
        if (o.temperatureSection !== undefined && o.temperatureSection !== -1) {
          counts.set(o.temperatureSection, (counts.get(o.temperatureSection) || 0) + 1);
        }
      }
      const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
      if (sorted.length === 0) return null;
      const [section, count] = sorted[0];
      return {
        label: TEMP_LABELS[section] || '未知',
        count,
      };
    },

    monthlyOutfitCounts() {
      const counts = new Array(12).fill(0);
      for (const o of this.outfitsThisYear) {
        if (o.date) {
          const month = parseInt(o.date.slice(5, 7), 10) - 1;
          if (month >= 0 && month < 12) counts[month]++;
        }
      }
      return counts;
    },
    totalMonthlyCount() {
      return this.monthlyOutfitCounts.reduce((a, b) => a + b, 0);
    },
    maxMonthlyCount() {
      return Math.max(...this.monthlyOutfitCounts, 1);
    },
    peakMonth() {
      let max = 0, idx = 0;
      this.monthlyOutfitCounts.forEach((c, i) => {
        if (c > max) { max = c; idx = i; }
      });
      return { label: `${idx + 1} 月`, count: max };
    },

    feelingDist() {
      let liked = 0, neutral = 0, disliked = 0;
      for (const o of this.outfitsThisYear) {
        if (o.feelingUnknown) continue;
        if (o.feeling === 1) liked++;
        else if (o.feeling === 0) neutral++;
        else if (o.feeling === -1) disliked++;
      }
      return { liked, neutral, disliked };
    },
    totalRatedOutfits() {
      return this.feelingDist.liked + this.feelingDist.neutral + this.feelingDist.disliked;
    },
    feelingPct() {
      const total = this.totalRatedOutfits || 1;
      return {
        liked: this.feelingDist.liked / total * 100,
        neutral: this.feelingDist.neutral / total * 100,
        disliked: this.feelingDist.disliked / total * 100,
      };
    },

    heroMsg() {
      const n = this.outfitsThisYear.length;
      if (n === 0) return `${this.selectedYear} 年還沒有穿搭紀錄`;
      if (n < 30) return '剛起步的一年，繼續加油！';
      if (n < 100) return '已經養成記錄習慣了';
      if (n < 200) return '熱愛穿搭的一年';
      return '超級重度玩家的一年！';
    },
  },
  methods: {
    barHeight(count) {
      const max = this.maxMonthlyCount;
      if (max === 0) return '0%';
      return `${count / max * 100}%`;
    },
    thumbStyle(cloth) {
      const url = (cloth.imgs && cloth.imgs[0]) || '';
      return url ? { backgroundImage: `url(${url})` } : { backgroundColor: '#F4F4F4' };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/basic";

.analyze-year-review {
  padding: .5rem 0 2rem;
}

// ===== 年份切換 =====
.year-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  margin-bottom: 1.25rem;
}
.year-tab {
  padding: .4rem 1rem;
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: 999px;
  color: $typography-secondary-default;
  font-size: 14px;
  cursor: pointer;
  transition: all .15s ease;
  &:hover {
    background-color: rgba(0, 0, 0, .03);
  }
  &.active {
    background-color: $color-primary;
    border-color: $color-primary;
    color: #fff;
    font-weight: bold;
  }
}

// ===== Hero 區 =====
.hero-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 0 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
}
.hero-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
}
.hero-stat {
  text-align: center;
}
.hero-num {
  font-size: 3rem;
  font-weight: bold;
  color: $color-primary;
  line-height: 1;
}
.hero-label {
  color: $typography-primary-default;
  margin-top: .5rem;
}
.hero-divider {
  width: 1px;
  height: 50px;
  background-color: $color-border;
}
.hero-msg {
  color: $typography-secondary-default;
  margin: 0;
}

// ===== 卡片 grid =====
.layout-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.span-2 {
  @media (min-width: 576px) {
    grid-column: span 2;
  }
}
.review-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1.25rem;

  .section-title {
    margin: 0 0 1rem;
    color: $typography-primary-default;
  }
}

// ===== Top cloths =====
.top-cloths {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}
.top-cloth-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  .rank {
    color: $color-primary;
    flex: 0 0 1.5rem;
    text-align: center;
  }
  .cloth-thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-color: #F4F4F4;
    border-radius: .5rem;
    border: 1px solid $color-border;
  }
  .cloth-info {
    flex: 1 1 auto;
    min-width: 0;
    .cloth-name {
      color: $typography-primary-default;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .cloth-meta {
      color: $typography-secondary-default;
      margin-top: 2px;
    }
  }
}

// ===== 月份 bar chart =====
.monthly-bars {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 4px;
  align-items: flex-end;
  height: 120px;
  margin-bottom: .75rem;
}
.month-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.month-bar-wrap {
  height: 100px;
  width: 100%;
  display: flex;
  align-items: flex-end;
}
.month-bar {
  width: 100%;
  background: linear-gradient(180deg, #F8B25C 0%, $color-primary 100%);
  border-radius: 3px 3px 0 0;
  min-height: 2px;
  transition: height .4s ease;
}
.month-label {
  color: $typography-secondary-default;
}
.monthly-summary {
  color: $typography-secondary-default;
  text-align: right;
}

// ===== 排行 + 長條圖（場景 / 色系共用） =====
.rank-bar-list {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}
.rank-bar-row {
  .rank-bar-name {
    margin-bottom: .25rem;
    color: $typography-primary-default;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    .color-dot {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 1px solid transparent;
      margin-right: .5rem;
      flex: 0 0 14px;
    }
  }
}

// ===== 溫度顯示 =====
.temp-display {
  text-align: center;
  padding: .75rem 0;
  .temp-label {
    color: $color-primary;
    margin-bottom: .25rem;
  }
  .temp-count {
    color: $typography-secondary-default;
  }
}

// ===== 好感度 =====
.feeling-bar {
  display: flex;
  height: 16px;
  border-radius: 8px;
  overflow: hidden;
  background-color: $color-border;
  margin-bottom: .75rem;
}
.feeling-segment {
  height: 100%;
  transition: width .4s ease;
  &.liked { background-color: #7DC4B7; }
  &.neutral { background-color: #F2C31C; }
  &.disliked { background-color: #DE6167; }
}
.feeling-legend {
  display: flex;
  justify-content: space-between;
  color: $typography-secondary-default;
  flex-wrap: wrap;
  gap: .5rem;
  > span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    &.liked { background-color: #7DC4B7; }
    &.neutral { background-color: #F2C31C; }
    &.disliked { background-color: #DE6167; }
  }
}

// ===== 空狀態 =====
.empty-state {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 3rem 1.25rem;
  text-align: center;
  color: $typography-secondary-default;
  p { margin: 0; }
}
</style>
